declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

export interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

const GA_MEASUREMENT_ID = "G-4FH3VQYFV7";

export const analytics = {
  pageView: (url: string, title?: string) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", GA_MEASUREMENT_ID, {
        page_path: url,
        page_title: title,
      });
    }
  },

  event: (action: string, category: string, label?: string, value?: number) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  },

  track: (eventName: string, params?: EventParams) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, params);
    }
  },

  setUserId: (userId: string) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("set", { user_id: userId });
    }
  },

  setUserProperties: (properties: EventParams) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("set", { user_properties: properties });
    }
  },

  timing: (name: string, value: number, category?: string) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "timing_complete", {
        name,
        value,
        event_category: category,
      });
    }
  },
};

export const ANALYTICS_EVENTS = {
  AUTH: {
    LOGIN: "login",
    LOGIN_SUCCESS: "login_success",
    LOGIN_FAILED: "login_failed",
    REGISTER: "register",
    REGISTER_SUCCESS: "register_success",
    REGISTER_FAILED: "register_failed",
    LOGOUT: "logout",
    VERIFY_EMAIL: "verify_email",
    FORGOT_PASSWORD: "forgot_password",
    RESET_PASSWORD: "reset_password",
    TWO_FACTOR_ENABLED: "2fa_enabled",
    TWO_FACTOR_VERIFIED: "2fa_verified",
    GOOGLE_LOGIN: "google_login",
    GOOGLE_LOGIN_SUCCESS: "google_login_success",
  },
  CV_BUILDER: {
    EDITOR_OPENED: "cv_editor_opened",
    CREATE_RESUME_CLICKED: "create_resume_clicked",
    UPLOAD_CV_CLICKED: "upload_cv_clicked",
    CV_UPLOAD_SUCCESS: "cv_upload_success",
    CV_UPLOAD_FAILED: "cv_upload_failed",
    CV_CREATED: "cv_created",
    CV_SAVED: "cv_saved",
    CV_DOWNLOADED: "cv_downloaded",
    CV_SHARED: "cv_shared",
    TEMPLATE_SELECTED: "template_selected",
    TEMPLATE_CHANGED: "template_changed",
    SECTION_ADDED: "section_added",
    SECTION_REMOVED: "section_removed",
    SECTION_REORDERED: "section_reordered",
    AI_ANALYSIS_RUN: "ai_analysis_run",
    AI_SUGGESTION_ACCEPTED: "ai_suggestion_accepted",
    AI_SUGGESTION_DISMISSED: "ai_suggestion_dismissed",
    PDF_PREVIEW_OPENED: "pdf_preview_opened",
    PDF_EXPORTED: "pdf_exported",
  },
  TEMPLATES: {
    BROWSE: "browse_templates",
    PREVIEW: "template_preview",
    SELECT: "template_select",
  },
  BLOG: {
    POST_VIEW: "blog_post_view",
    POST_LIKED: "blog_post_liked",
    COMMENT_ADDED: "blog_comment_added",
  },
  FEEDBACK: {
    SUBMITTED: "feedback_submitted",
    CATEGORY_SELECTED: "feedback_category_selected",
  },
  NAVIGATION: {
    CTA_CLICKED: "cta_clicked",
    BUTTON_CLICKED: "button_clicked",
    LINK_CLICKED: "link_clicked",
    EXTERNAL_LINK_CLICKED: "external_link_clicked",
  },
  LANDING: {
    HERO_CTA_CLICKED: "hero_cta_clicked",
    FEATURE_EXPLORED: "feature_explored",
    PRICING_VIEWED: "pricing_viewed",
    FAQ_EXPANDED: "faq_expanded",
  },
} as const;

export const trackAuth = {
  login: (method: "email" | "google") => {
    analytics.event(
      ANALYTICS_EVENTS.AUTH.LOGIN,
      "Authentication",
      `method:${method}`,
    );
  },
  loginSuccess: (method: "email" | "google", userId?: string) => {
    if (userId) analytics.setUserId(userId);
    analytics.event(
      ANALYTICS_EVENTS.AUTH.LOGIN_SUCCESS,
      "Authentication",
      `method:${method}`,
    );
  },
  loginFailed: (method: "email" | "google") => {
    analytics.event(
      ANALYTICS_EVENTS.AUTH.LOGIN_FAILED,
      "Authentication",
      `method:${method}`,
    );
  },
  register: (method: "email" | "google") => {
    analytics.event(
      ANALYTICS_EVENTS.AUTH.REGISTER,
      "Authentication",
      `method:${method}`,
    );
  },
  registerSuccess: (method: "email" | "google", userId?: string) => {
    if (userId) analytics.setUserId(userId);
    analytics.event(
      ANALYTICS_EVENTS.AUTH.REGISTER_SUCCESS,
      "Authentication",
      `method:${method}`,
    );
  },
  logout: () => {
    analytics.event(ANALYTICS_EVENTS.AUTH.LOGOUT, "Authentication");
  },
  verifyEmail: () => {
    analytics.event(ANALYTICS_EVENTS.AUTH.VERIFY_EMAIL, "Authentication");
  },
  forgotPassword: () => {
    analytics.event(ANALYTICS_EVENTS.AUTH.FORGOT_PASSWORD, "Authentication");
  },
  resetPassword: () => {
    analytics.event(ANALYTICS_EVENTS.AUTH.RESET_PASSWORD, "Authentication");
  },
  twoFactorEnabled: () => {
    analytics.event(ANALYTICS_EVENTS.AUTH.TWO_FACTOR_ENABLED, "Authentication");
  },
  twoFactorVerified: () => {
    analytics.event(
      ANALYTICS_EVENTS.AUTH.TWO_FACTOR_VERIFIED,
      "Authentication",
    );
  },
};

export const trackCVBuilder = {
  editorOpened: (source: "new" | "existing" | "template") => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.EDITOR_OPENED,
      "CV Builder",
      `source:${source}`,
    );
  },
  createResumeClicked: () => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.CREATE_RESUME_CLICKED,
      "CV Builder",
    );
  },
  uploadCVClicked: () => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.UPLOAD_CV_CLICKED,
      "CV Builder",
    );
  },
  cvUploadSuccess: (fileName: string) => {
    analytics.track(ANALYTICS_EVENTS.CV_BUILDER.CV_UPLOAD_SUCCESS, {
      file_name: fileName,
    });
  },
  cvUploadFailed: (error?: string) => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.CV_UPLOAD_FAILED,
      "CV Builder",
      error,
    );
  },
  cvCreated: (templateId?: string) => {
    analytics.track(ANALYTICS_EVENTS.CV_BUILDER.CV_CREATED, {
      template_id: templateId,
    });
  },
  cvSaved: (autoSave: boolean) => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.CV_SAVED,
      "CV Builder",
      autoSave ? "auto" : "manual",
    );
  },
  cvDownloaded: (format: "pdf" | "docx") => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.CV_DOWNLOADED,
      "CV Builder",
      `format:${format}`,
    );
  },
  cvShared: () => {
    analytics.event(ANALYTICS_EVENTS.CV_BUILDER.CV_SHARED, "CV Builder");
  },
  templateSelected: (templateId: string, templateName: string) => {
    analytics.track(ANALYTICS_EVENTS.CV_BUILDER.TEMPLATE_SELECTED, {
      template_id: templateId,
      template_name: templateName,
    });
  },
  templateChanged: (fromTemplate: string, toTemplate: string) => {
    analytics.track(ANALYTICS_EVENTS.CV_BUILDER.TEMPLATE_CHANGED, {
      from_template: fromTemplate,
      to_template: toTemplate,
    });
  },
  sectionAdded: (sectionName: string) => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.SECTION_ADDED,
      "CV Builder",
      sectionName,
    );
  },
  sectionRemoved: (sectionName: string) => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.SECTION_REMOVED,
      "CV Builder",
      sectionName,
    );
  },
  aiAnalysisRun: (atsScore?: number) => {
    analytics.track(ANALYTICS_EVENTS.CV_BUILDER.AI_ANALYSIS_RUN, {
      ats_score: atsScore,
    });
  },
  aiSuggestionAccepted: (sectionName: string) => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.AI_SUGGESTION_ACCEPTED,
      "AI Assistant",
      sectionName,
    );
  },
  aiSuggestionDismissed: (sectionName: string) => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.AI_SUGGESTION_DISMISSED,
      "AI Assistant",
      sectionName,
    );
  },
  pdfPreviewOpened: () => {
    analytics.event(
      ANALYTICS_EVENTS.CV_BUILDER.PDF_PREVIEW_OPENED,
      "CV Builder",
    );
  },
  pdfExported: () => {
    analytics.event(ANALYTICS_EVENTS.CV_BUILDER.PDF_EXPORTED, "CV Builder");
  },
};

export const trackTemplates = {
  browse: () => {
    analytics.event(ANALYTICS_EVENTS.TEMPLATES.BROWSE, "Templates");
  },
  pageViewed: () => {
    analytics.event(
      ANALYTICS_EVENTS.TEMPLATES.BROWSE,
      "Templates",
      "page_view",
    );
  },
  templateSearched: (query: string, resultsCount: number) => {
    analytics.track(ANALYTICS_EVENTS.TEMPLATES.BROWSE, {
      search_query: query,
      results_count: resultsCount,
    });
  },
  filterApplied: (filterType: string, filterValue: string) => {
    analytics.event(
      ANALYTICS_EVENTS.TEMPLATES.BROWSE,
      "Templates",
      `filter:${filterType}=${filterValue}`,
    );
  },
  preview: (templateId: string, templateName: string) => {
    analytics.track(ANALYTICS_EVENTS.TEMPLATES.PREVIEW, {
      template_id: templateId,
      template_name: templateName,
    });
  },
  select: (templateId: string, templateName: string) => {
    analytics.track(ANALYTICS_EVENTS.TEMPLATES.SELECT, {
      template_id: templateId,
      template_name: templateName,
    });
  },
};

export const trackBlog = {
  blogPageViewed: () => {
    analytics.event(ANALYTICS_EVENTS.BLOG.POST_VIEW, "Blog", "page_view");
  },
  postView: (postSlug: string, postTitle: string) => {
    analytics.track(ANALYTICS_EVENTS.BLOG.POST_VIEW, {
      post_slug: postSlug,
      post_title: postTitle,
    });
  },
  postLiked: (postSlug: string) => {
    analytics.event(ANALYTICS_EVENTS.BLOG.POST_LIKED, "Blog", postSlug);
  },
  commentAdded: (postSlug: string) => {
    analytics.event(ANALYTICS_EVENTS.BLOG.COMMENT_ADDED, "Blog", postSlug);
  },
};

export const trackFeedback = {
  submitted: (category?: string) => {
    analytics.track(ANALYTICS_EVENTS.FEEDBACK.SUBMITTED, {
      category,
    });
  },
  categorySelected: (category: string) => {
    analytics.event(
      ANALYTICS_EVENTS.FEEDBACK.CATEGORY_SELECTED,
      "Feedback",
      category,
    );
  },
};

export const trackNavigation = {
  ctaClicked: (ctaName: string, location: string) => {
    analytics.track(ANALYTICS_EVENTS.NAVIGATION.CTA_CLICKED, {
      cta_name: ctaName,
      location,
    });
  },
  buttonClicked: (buttonName: string, page: string) => {
    analytics.track(ANALYTICS_EVENTS.NAVIGATION.BUTTON_CLICKED, {
      button_name: buttonName,
      page,
    });
  },
  linkClicked: (linkText: string, url: string) => {
    analytics.track(ANALYTICS_EVENTS.NAVIGATION.LINK_CLICKED, {
      link_text: linkText,
      url,
    });
  },
  externalLinkClicked: (url: string, domain: string) => {
    analytics.track(ANALYTICS_EVENTS.NAVIGATION.EXTERNAL_LINK_CLICKED, {
      url,
      domain,
    });
  },
};

export const trackLanding = {
  heroCtaClicked: (ctaType: "get_started" | "try_free" | "learn_more") => {
    analytics.track(ANALYTICS_EVENTS.LANDING.HERO_CTA_CLICKED, {
      cta_type: ctaType,
    });
  },
  featureExplored: (featureName: string) => {
    analytics.event(
      ANALYTICS_EVENTS.LANDING.FEATURE_EXPLORED,
      "Landing Page",
      featureName,
    );
  },
  pricingViewed: () => {
    analytics.event(ANALYTICS_EVENTS.LANDING.PRICING_VIEWED, "Landing Page");
  },
  faqExpanded: (questionIndex: number) => {
    analytics.event(
      ANALYTICS_EVENTS.LANDING.FAQ_EXPANDED,
      "Landing Page",
      `question_${questionIndex}`,
    );
  },
};
